import React, { FC, useMemo } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Center,
  Divider,
  Skeleton,
  SkeletonCircle,
  SkeletonText,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';

import { ArcButton } from '../../../common/components/ArcButton';
import { SlidingAnimationPageBase } from '../../../common/components/SlidingAnimationPageBase';
import { IS_NATIVE } from '../../../common/constants';
import { DEVICE_ICON_RESPONSIVE_SIZE } from '../devices-helper';
import { DeviceStatusIcon } from '../DeviceStatusIcon';
import { useDeleteAylaDeviceMutation } from '../switches/switchApi';
import { getAylaDeviceType, useAylaSwitchData, useTranslatedUIConfig } from './ayla-switch-helpers';
import {
  useDeleteLocalAylaMatterDeviceMutation,
  useGetLocalAylaMatterDevice,
} from './matter/localAylaMatterDevicesApi';
import { ShareMatterPairCodeButton } from './matter/ShareMatterPairCodeButton';

export function AylaDeviceDetails() {
  const navigate = useNavigate();
  const params = useParams<{ switchId: string }>();
  const switchId = Number(params.switchId);
  const toast = useToast({ isClosable: true });
  const { t } = useTranslation();
  const { isOpen: isDeleteAlertOpen, onOpen: onOpenDeleteAlert, onClose: onCloseDeleteAlert } = useDisclosure();
  const { data: aylaSwitch, isLiveDataLoading, isLoading, isApiSwitchError } = useAylaSwitchData(switchId);
  const { display_name: displayName, state, site_switch_label: siteSwitchLabel } = aylaSwitch;
  const { deviceType, isMatterDevice } = getAylaDeviceType(displayName);
  const { data: localMatterDevice, isLoading: isLocalDataLoading } = useGetLocalAylaMatterDevice(
    aylaSwitch?.oem_switch_id,
    isLoading || !isMatterDevice
  );
  const [deleteLocalMatterDevice, { isLoading: isLocalDeleteLoading }] = useDeleteLocalAylaMatterDeviceMutation();
  const [deleteAylaDevice, { isLoading: isApiDeleteLoading }] = useDeleteAylaDeviceMutation();
  const isDeleteLoading = isLocalDeleteLoading || isApiDeleteLoading;
  const AYLA_DEVICES_UI_CONFIG = useTranslatedUIConfig();

  const isMatterDeviceCommissionedLocally = isMatterDevice && IS_NATIVE && localMatterDevice?.reachable === true;

  const handleUnavailableFeatureClick = () => {
    if (!isMatterDeviceCommissionedLocally) {
      toast({
        title: t('Devices.feature only available if commissioned locally'),
        status: 'info',
        id: 'not-commissioned-locally-toast',
      });
    }
  };

  const handleRemoveDevice = async () => {
    try {
      /* istanbul ignore if -- @preserve */
      if (isMatterDeviceCommissionedLocally) {
        await deleteLocalMatterDevice({
          matterDeviceId: localMatterDevice.matterDeviceId,
        }).unwrap();
      }
      await deleteAylaDevice({
        switchId,
      }).unwrap();
      toast({
        title: t('Devices.device deleted toast title'),
        status: 'success',
      });
      navigate(`../../../devices/list?direction=back`);
    } catch (error) {
      console.error('Something went wrong removing Matter device... ', error);
      toast({
        title: t('Devices.error removing device'),
        description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        status: 'error',
      });
    }
  };

  const backURL = useMemo(
    () => (isApiSwitchError ? `../../../devices/list?direction=back` : `../view?direction=back`),
    [isApiSwitchError]
  );

  return (
    <SlidingAnimationPageBase backURL={backURL} includeBottomNav title={t('Devices.device specs')}>
      <Box data-testid="ayla-device-detail-contents" px={4}>
        {isApiSwitchError ? (
          <Alert status="error" variant="left-accent" data-testid="error-alert">
            <AlertIcon />
            <Box>
              <AlertTitle>{t('Common.notice')}</AlertTitle>
              <AlertDescription>
                {t('Devices.error fetching device')}.{' '}
                {`${t('Common.please try again')} ${t('Common.if this persists contact support')}`}
              </AlertDescription>
            </Box>
          </Alert>
        ) : (
          <>
            <SkeletonCircle
              isLoaded={!isLiveDataLoading}
              w={DEVICE_ICON_RESPONSIVE_SIZE}
              h={DEVICE_ICON_RESPONSIVE_SIZE}
              m="auto"
            >
              {aylaSwitch && (
                <DeviceStatusIcon isOn={state === 'closed'} icon={AYLA_DEVICES_UI_CONFIG[deviceType].icon} />
              )}
            </SkeletonCircle>
            <Skeleton isLoaded={!isLoading} my={2}>
              <Text minH="30px" fontSize={24} data-testid="switch-name" overflowWrap="anywhere" textAlign="center">
                {siteSwitchLabel}
              </Text>
            </Skeleton>

            {isMatterDevice && (
              <>
                <Divider my={4} />
                <ShareMatterPairCodeButton
                  matterDeviceId={localMatterDevice?.matterDeviceId}
                  isCommissionedLocally={isMatterDeviceCommissionedLocally}
                  isParentLoaded={!isLocalDataLoading}
                />
                <Divider my={4} />
              </>
            )}

            <DeviceDetailRow
              label={t('Common.serial number')}
              value={aylaSwitch?.oem_switch_id}
              isLoaded={!isLoading}
              testIdSuffix="serial-number"
            />

            {isMatterDevice && (
              <DeviceDetailRow
                label={t('Common.model name')}
                value={
                  localMatterDevice?.productName || (
                    <Badge onClick={handleUnavailableFeatureClick} data-testid="model-name">
                      {t('Common.not found')}
                    </Badge>
                  )
                }
                isLoaded={!isLocalDataLoading}
                testIdSuffix="model-name"
              />
            )}

            {isMatterDevice && (
              <DeviceDetailRow
                label={t('Common.model number')}
                value={
                  localMatterDevice?.partNumber || (
                    <Badge onClick={handleUnavailableFeatureClick} data-testid="empty-model-number">
                      {t('Common.not found')}
                    </Badge>
                  )
                }
                isLoaded={!isLocalDataLoading}
                testIdSuffix="model-number"
              />
            )}

            <DeviceDetailRow
              label={t('Common.description')}
              value={AYLA_DEVICES_UI_CONFIG[deviceType].description}
              isLoaded={!isLoading}
              testIdSuffix="description"
              LoaderComponent={SkeletonText}
            />

            {isMatterDevice && (
              <DeviceDetailRow
                label={t('Common.mac address')}
                value={
                  localMatterDevice?.macAddress || (
                    <Badge onClick={handleUnavailableFeatureClick} data-testid="empty-mac-address">
                      {t('Common.not found')}
                    </Badge>
                  )
                }
                isLoaded={!isLocalDataLoading}
                testIdSuffix="mac-address"
              />
            )}

            {isMatterDevice && (
              <DeviceDetailRow
                label={t('Common.firmware')}
                value={
                  localMatterDevice?.softwareVersionString || (
                    <Badge onClick={handleUnavailableFeatureClick} data-testid="empty-firmware">
                      {t('Common.not found')}
                    </Badge>
                  )
                }
                isLoaded={!isLocalDataLoading}
                testIdSuffix="firmware"
              />
            )}

            <Center flexDir="column">
              <ArcButton
                arcColor="#FF5B44"
                width="80%"
                data-testid="remove-device-button"
                onClick={onOpenDeleteAlert}
                isDisabled={isDeleteLoading}
                isLoading={isLocalDataLoading || isLoading || isDeleteLoading}
                mt={6}
              >
                {t('Devices.delete device')}
              </ArcButton>
              <AlertDialogModal
                isOpen={isDeleteAlertOpen}
                onClose={onCloseDeleteAlert}
                header={t('Devices.delete device modal title', { deviceName: aylaSwitch?.site_switch_label })}
                subHeader={t('Devices.delete device modal text')}
                confirmButtonName={t('Common.delete')}
                cancelButtonName={t('Common.cancel')}
                onConfirm={handleRemoveDevice}
              />
            </Center>
          </>
        )}
      </Box>
    </SlidingAnimationPageBase>
  );
}

const DeviceDetailRow: FC<{
  label: string;
  value: React.ReactNode;
  isLoaded: boolean;
  testIdSuffix: string;
  LoaderComponent?: typeof Skeleton | typeof SkeletonText;
}> = ({ label, value, isLoaded, testIdSuffix, LoaderComponent = Skeleton }) => (
  <Box my={4}>
    <Text fontWeight="bold" data-testid={`${testIdSuffix}-title`}>
      {label}
    </Text>
    <LoaderComponent isLoaded={isLoaded} minHeight="1.25rem">
      <Box data-testid={`${testIdSuffix}-text`}>{value}</Box>
    </LoaderComponent>
  </Box>
);
